// @core/stores/payment/PaymentPaymentPolicyStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { paymentApi } from '@core/api';
import type {
  CreatePaymentPolicyRequest,
  PaymentPolicy,
  ResolvePaymentContextRequest,
  ResolvePaymentContextResponse,
  UpdatePaymentPolicyRequest,
} from '@core/types/payment';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class PaymentPaymentPolicyStore {
  // List state
  items: PaymentPolicy[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: PaymentPolicy | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, PaymentPolicy>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): PaymentPolicy | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: PaymentPolicy | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create payment policy
   */
  async CreatePaymentPolicy(data: CreatePaymentPolicyRequest): Promise<PaymentPolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await paymentApi.CreatePaymentPolicy(data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete payment policy
   */
  async DeletePaymentPolicy(policy_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await paymentApi.DeletePaymentPolicy(policy_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== policy_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === policy_id) {
          this.currentItem = null;
        }

        this.removeFromCache(policy_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get payment policy
   */
  async GetPaymentPolicy(policy_id: string): Promise<PaymentPolicy> {
    // Check cache first
    const cached = this.cache.get(policy_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await paymentApi.GetPaymentPolicy(policy_id);
      const item = response.policy;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List payment policies
   */
  async ListPaymentPolicies(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await paymentApi.ListPaymentPolicies(params);

      runInAction(() => {
        this.items = response.payment_policies?.policies ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resolve payment context
   */
  async ResolvePaymentContext(data: ResolvePaymentContextRequest): Promise<ResolvePaymentContextResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.ResolvePaymentContext(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update payment policy
   */
  async UpdatePaymentPolicy(policy_id: string, data: UpdatePaymentPolicyRequest): Promise<PaymentPolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await paymentApi.UpdatePaymentPolicy(policy_id, data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const paymentPaymentPolicyStore = new PaymentPaymentPolicyStore();
